/**
 * Floating "Transferred from AI assistant" card on the agent's ringing /
 * active call. A phone hand-off keeps the caller's calls row, so the AI
 * session is found by that callId (GET /calls/:callId/ai-session). Shows
 * the AI's reason + note for the agent, with the full transcript on demand.
 */
import { useEffect, useState, type JSX } from 'react'
import { Bot, ChevronDown, ChevronUp, X } from 'lucide-react'
import { api } from '../../api/client.js'
import { AiCallPanel, type AiSession } from './AiCallPanel.js'
import { Modal } from '../wfm/WfmUi.js'

export default function AiHandoffCard({ callId }: { callId: string | null }): JSX.Element | null {
  const [session, setSession] = useState<AiSession | null>(null)
  const [hidden, setHidden] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [full, setFull] = useState(false)

  useEffect(() => {
    setSession(null)
    setHidden(false)
    if (!callId) return
    let cancelled = false
    let tries = 0
    // The hand-off fields are written a moment before the call rings; retry
    // briefly so a fast ring still gets its card.
    const load = (): void => {
      void api.get<{ item: AiSession | null }>(`/calls/${callId}/ai-session`)
        .then((r) => {
          if (cancelled) return
          if (r.item && (r.item.handoffSummary || r.item.handoffReason || r.item.status === 'HANDED_OFF')) setSession(r.item)
          else if (r.item && tries++ < 3) setTimeout(load, 1500)
        })
        .catch(() => { /* not an AI hand-off */ })
    }
    load()
    return () => { cancelled = true }
  }, [callId])

  if (!session || hidden) return null
  return (
    <>
      <div className="fixed bottom-24 right-6 z-[900] w-[340px] overflow-hidden rounded-[3px] bg-white shadow-[0_16px_48px_-12px_rgba(15,23,42,0.35)] ring-1 ring-blue-200">
        <header className="flex items-center gap-2 bg-[#EEF4FF] px-3 py-2">
          <Bot className="h-4 w-4 text-[#3366FF]" />
          <p className="flex-1 text-[12.5px] font-semibold text-slate-900">Transferred from {session.agentName ?? 'AI assistant'}</p>
          <button type="button" aria-label={collapsed ? 'Expand' : 'Collapse'} className="rounded-[3px] p-1 text-slate-500 hover:bg-white" onClick={() => setCollapsed((v) => !v)}>
            {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button type="button" aria-label="Dismiss" className="rounded-[3px] p-1 text-slate-500 hover:bg-white" onClick={() => setHidden(true)}><X className="h-4 w-4" /></button>
        </header>
        {!collapsed && (
          <div className="space-y-2 px-3 py-3 text-[12.5px] text-slate-700">
            {session.handoffReason && <p><span className="font-semibold text-slate-900">Reason:</span> {session.handoffReason}</p>}
            {session.handoffSummary && <p className="leading-relaxed">{session.handoffSummary}</p>}
            <button type="button" className="text-[12px] font-semibold text-[#3366FF] hover:underline" onClick={() => {
              void api.get<{ item: AiSession | null }>(`/calls/${callId}/ai-session`).then((r) => { if (r.item) setSession(r.item) })
              setFull(true)
            }}>View the full AI conversation</button>
          </div>
        )}
      </div>
      {full && (
        <Modal open width="max-w-3xl" title="AI conversation before the transfer" onClose={() => setFull(false)}>
          <AiCallPanel session={session} showRecording={false} />
        </Modal>
      )}
    </>
  )
}
